import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  CreatedBy,
  LoginResponse,
  SetNewPasswordRequest,
  TwoFAResponseModel,
  UserModel,
  UserWithPhoneNumbersAndLoginVerificationCode,
} from '../model/userModel';
import { BehaviorSubject, catchError, map } from 'rxjs';
import { Router } from '@angular/router';
import { IdNameModel } from '../model/IdNameModel';
import { environment } from '../../environments/environment';
import { LocalStorageToken } from '../util/constant';
import { SnackbarService } from './snackbar.service';
import { FilterService } from './filter.service';

interface UserWithRoles extends UserModel {
  roles: IdNameModel[];
}

export interface ResetPasswordDto {
  newPassword: string;
  newPasswordConfirm: string;
  token: string;
}

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  baseUrl = environment.apiBaseUrl + '/api/account';
  filterService = inject(FilterService);
  private http = inject(HttpClient);
  router = inject(Router);
  private snackbarService = inject(SnackbarService);
  isLoading = false;

  private twoFAUser = new BehaviorSubject<UserWithPhoneNumbersAndLoginVerificationCode | null>(
    null,
  );
  twoFAUser$ = this.twoFAUser.asObservable();

  get twoFAUserValue(): UserWithPhoneNumbersAndLoginVerificationCode | null {
    return this.twoFAUser.value;
  }

  setTwoFAUser(user: UserWithPhoneNumbersAndLoginVerificationCode | null) {
    this.twoFAUser.next(user);
  }

  private userSubject = new BehaviorSubject<UserWithRoles | null>(null);
  user$ = this.userSubject.asObservable();

  get user(): UserWithRoles | null {
    return this.userSubject.value;
  }

  setUser(user: UserWithRoles | null) {
    this.userSubject.next(user);
  }

  login(username: string | null | undefined, password: string | null | undefined) {
    this.isLoading = true;
    return this.http
      .post<LoginResponse>(`${this.baseUrl}/login`, {
        username,
        password,
      })
      .pipe(
        map((response) => {
          this.setTwoFAUser(response.user);
          this.isLoading = false;
          return response;
        }),
        catchError((error) => {
          this.isLoading = false;
          console.error('Login error:', error);
          throw error;
        }),
      );
  }

  getLoginCodeFor2FA(phoneNumber: string | null | undefined) {
    this.isLoading = true;
    if (!this.twoFAUserValue) {
      throw new Error('No user information available for 2FA login.');
    }
    return this.http
      .post<TwoFAResponseModel>(`${this.baseUrl}/send-login-verification-code`, {
        userId: this.twoFAUserValue.id,
        loginVerificationCode: this.twoFAUserValue.loginVerificationCode,
        phoneNumber,
      })
      .pipe(
        map((response) => {
          this.isLoading = false;
          return response;
        }),
        catchError((error) => {
          this.isLoading = false;
          console.error('Get 2FA code error:', error);
          throw error;
        }),
      );
  }

  loginWithTwoFA(verificationCode: string | null | undefined) {
    this.isLoading = true;
    if (!this.twoFAUserValue) {
      throw new Error('No user information available for 2FA login.');
    }
    return this.http
      .post<TwoFAResponseModel>(`${this.baseUrl}/login/2fa`, {
        userId: this.twoFAUserValue.id,
        loginVerificationCode: this.twoFAUserValue.loginVerificationCode,
        verificationCode,
      })
      .pipe(
        map((response) => {
          localStorage.setItem(LocalStorageToken, response.token);
          this.getUser();
          return response;
        }),
        catchError((error) => {
          setTimeout(() => {
            this.isLoading = false;
          });
          console.error('Login error:', error);
          throw error;
        }),
      );
  }

  changePassword(
    oldPassword: string | null | undefined,
    newPassword: string | null | undefined,
    newPasswordConfirm: string | null | undefined,
  ) {
    this.isLoading = true;
    return this.http
      .patch(`${this.baseUrl}/change-password`, {
        oldPassword,
        newPassword,
        newPasswordConfirm,
      })
      .pipe(
        map((response) => {
          this.isLoading = false;
          return response;
        }),
        catchError((error) => {
          this.isLoading = false;
          console.error('Change password error:', error);
          throw error;
        }),
      );
  }

  logout() {
    this.isLoading = true;
    return this.http.post(`${this.baseUrl}/logout`, {}).subscribe({
      next: () => {
        localStorage.removeItem(LocalStorageToken);
        this.router.navigate(['/login']);
        this.setUser(null);
      },
      error: (error) => {
        console.error('API error:', error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  getUser() {
    this.isLoading = true;
    return this.http.get<UserModel>(`${this.baseUrl}/get-current-user`).subscribe({
      next: (response) => {
        if (response != null) {
          this.setUser({
            ...response,
            roles: response.roles.concat(response.jobTitleRoles, response.departmentRoles),
          });
          this.filterService.getFilterValues().subscribe({
            next: (response) => {
              if (response.status === 'success') {
                this.filterService.filterValues = response.data;
              }
            },
          });
        }
      },
      error: (error) => {
        console.error('API error:', error);
        this.isLoading = false;
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  getVerificationCodeForNewAccountSetup(
    email: string | null | undefined,
    phoneNumber: string | null | undefined,
  ) {
    this.isLoading = true;
    return this.http
      .post<LoginResponse>(`${this.baseUrl}/new-account/verification`, { email, phoneNumber })
      .pipe(
        map((response) => {
          this.setTwoFAUser(response.data);
          this.isLoading = false;
          return response;
        }),
        catchError((error) => {
          this.isLoading = false;
          console.error('Get verification code error:', error);
          throw error;
        }),
      )
      .subscribe({
        next: () => {
          this.router.navigate(['/verify-new-account']);
        },
        error: (error) => {
          console.error('API error:', error);
        },
      });
  }

  verifyCodeForNewAccountSetup(verificationCode: string | null | undefined) {
    this.isLoading = true;
    return this.http
      .get<LoginResponse>(
        `${this.baseUrl}/new-account/${this.twoFAUserValue?.id}/verify-code/${verificationCode}`,
      )
      .subscribe({
        next: (response) => {
          this.router.navigate(['/set-new-password']);
        },
        error: (error) => {
          this.isLoading = false;
          console.error('API error:', error);
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  setNewPasswordForNewAccountSetup(newPasswords: SetNewPasswordRequest) {
    this.isLoading = true;
    return this.http
      .patch<LoginResponse>(
        `${this.baseUrl}/new-account/${this.twoFAUserValue?.id}/set-new-password`,
        newPasswords,
      )
      .subscribe({
        next: () => {
          localStorage.removeItem(LocalStorageToken);
          this.snackbarService.success(
            'Your password has been set successfully. Please log in with your new password.',
          );
          this.router.navigate(['/login']);
          this.setUser(null);
        },
        error: (error) => {
          console.error('API error:', error);
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  requestResetPassword(email: string) {
    this.isLoading = true;
    return this.http
      .get(`${this.baseUrl}/password-reset/send-verification-link`, {
        params: {
          email: email ?? '',
          requestOrigin: window.location.origin,
        },
      })
      .subscribe({
        next: () => {
          this.snackbarService.success(
            'If an account with that email exists, a password reset link has been sent.',
          );
          this.router.navigate(['/login']);
        },
        error: (error) => {
          console.error('API error:', error);
          this.snackbarService.error(
            'An error occurred while requesting password reset. Please try again later.',
          );
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  resetPasswordWithToken(newPasswords: ResetPasswordDto, userId: number) {
    this.isLoading = true;
    return this.http
      .patch(`${this.baseUrl}/password-reset/${userId}/set-new-password`, newPasswords)
      .subscribe({
        next: () => {
          this.snackbarService.success('Your password has been reset successfully. Please log in.');
          this.router.navigate(['/login']);
        },
        error: (error) => {
          console.error('API error:', error);
          this.snackbarService.error(
            'An error occurred while resetting your password. Please try again later.',
          );
        },
        complete: () => {
          this.isLoading = false;
        },
      });
  }
}
